import { MenuItemOptions } from "@finanedi.es/storybook"
import {
  faFileLines,
  faLinkSlash,
  faPen,
  faRotate,
  faTrashCan,
} from "@fortawesome/pro-light-svg-icons"
import { GenerarRequisitionLinkCommandProps } from "api/Commands/GenerarRequisitionLinkCommand"
import { GetCuentasBancariasByFiltersDtoProps } from "api/DTOs/GetCuentasBancariasByFiltersDto"
import { STATIC_ASSETS_URL } from "common/context/constants"
import {
  FechaBodyTemplate,
  ImageBodyTemplate,
  ImporteBodyTemplate,
} from "features/Documentos/templates"
import { TFunction } from "i18next"
import { Grid, Menu } from "react-feather"
import { BankAccountStatus } from "./components/BankAccountStatus"
import { CURRENT_DATE_FORMAT_WITH_24_HOURS } from "./components/BankCard/constants"

export const DEFAULT_BANK_LOGO_URL = `${STATIC_ASSETS_URL}/Imagenes/Ilustraciones/img-cuenta-bancaria.svg`

export const sumDisponible = (accounts: $TSMigrationFixMe) => {
  return accounts?.reduce(
    (acc: $TSMigrationFixMe, account: $TSMigrationFixMe) => {
      return acc + (account?.saldos?.disponible ?? 0)
    },
    0
  )
}

export const sumReal = (accounts: $TSMigrationFixMe) => {
  return accounts.reduce(
    (acc: $TSMigrationFixMe, account: $TSMigrationFixMe) => {
      return acc + (account?.saldos?.real ?? 0)
    },
    0
  )
}

export interface stateAutorizar {
  status: boolean
  movimientos: boolean
  id: string
  entidadBancariaNombre: string | null
  iban: string
  bic: string
}

export interface BankEntities {
  entidad: GetCuentasBancariasByFiltersDtoProps["entidadBancaria"]
  logo: GetCuentasBancariasByFiltersDtoProps["logo"]
}

export const TABLE_BANK_ACCOUNT_COLUMNS = (
  onRenderImportarButton: (
    rowData: GetCuentasBancariasByFiltersDtoProps
  ) => JSX.Element | null,
  onRenderActionIcons: (
    rowData: GetCuentasBancariasByFiltersDtoProps
  ) => JSX.Element,
  t: TFunction
) => {
  const isInterno = sessionStorage.getItem("isUserInterno") === "true"

  const columns = [
    {
      key: "logo",
      field: "logo",
      className: "!tw-p-2",
      style: { width: "5%" },
      body: (rowData: GetCuentasBancariasByFiltersDtoProps) =>
        ImageBodyTemplate({
          concepto: rowData.entidadBancaria?.nombre,
          src: rowData.logo || DEFAULT_BANK_LOGO_URL,
          warning: rowData,
          t,
          isDefaultLogo: !rowData.logo,
        }),
    },
    {
      key: "bankName",
      field: "bankName",
      style: { width: "5%" },
      header: `${t("bancos.table.headerEntBank")}`,
      body: ({ entidadBancaria }: GetCuentasBancariasByFiltersDtoProps) => (
        <span className='tw-text-nowrap tw-truncate tw-text-ellipsis'>
          {entidadBancaria?.nombre}
        </span>
      ),
    },
    {
      key: "nombre",
      field: "nombre",
      style: { width: "10%" },
      header: `${t("bancos.table.accountName")}`,
      body: ({ nombre }: GetCuentasBancariasByFiltersDtoProps) => (
        <span className='tw-truncate'>
          {nombre ? nombre.slice(0, 20) : "-"}
          {nombre && nombre.length > 20 && " ..."}
        </span>
      ),
    },
    {
      key: "iban",
      field: "iban",
      style: { width: "12%" },
      header: `${t("bancos.table.headerIban")}`,
    },
    {
      key: "balance",
      field: "balance",
      alignCol: "right",
      style: { width: "8%" },
      header: `${t("bancos.table.headerAvailableBalance")}`,
      body: ({ saldos }: GetCuentasBancariasByFiltersDtoProps) =>
        ImporteBodyTemplate({
          importe: saldos?.disponible ?? 0,
          className: "!tw-text-right !tw-block",
        }),
    },
    {
      key: "balanceContable",
      field: "balanceContable",
      style: { width: "8%" },
      alignCol: "right",
      header: `${t("bancos.table.headerBalanceContable")}`,
      body: ({ saldos }: GetCuentasBancariasByFiltersDtoProps) =>
        !saldos?.contable ? (
          <p className='tw-text-right'>-</p>
        ) : (
          ImporteBodyTemplate({
            importe: saldos.contable,
            className: "!tw-text-right !tw-block",
          })
        ),
    },
    {
      key: "fecha",
      header: `${t("bancos.table.headerupdateDate")}`,
      field: "fechaUltimaActualizacion",
      style: { width: "10%" },
      alignCol: "center",
      className: "!tw-text-center",
      body: ({
        fechaUltimaActualizacion,
      }: GetCuentasBancariasByFiltersDtoProps) => (
        <span>
          {fechaUltimaActualizacion
            ? FechaBodyTemplate(
                { fecha: fechaUltimaActualizacion },
                "",
                CURRENT_DATE_FORMAT_WITH_24_HOURS
              )
            : "-"}
        </span>
      ),
    },
    {
      key: "grantAccess",
      field: "grantAccess",
      style: { width: "10%" },
      body: (rowData: GetCuentasBancariasByFiltersDtoProps) =>
        onRenderImportarButton(rowData),
    },
    {
      key: "actions",
      field: "actions",
      style: { width: "4%" },
      className: "!tw-p-2",
      bodyClassName: "!tw-w-auto tw-flex tw-justify-end",
      body: (rowData: GetCuentasBancariasByFiltersDtoProps) =>
        onRenderActionIcons(rowData),
    },
  ]

  if (!isInterno) {
    columns.splice(4, 0, {
      key: "status",
      field: "status",
      alignCol: "center",
      className: "!tw-text-center",
      style: { width: "10%" },
      header: `${t("bancos.table.headerStatus")}`,
      body: ({ estado }: GetCuentasBancariasByFiltersDtoProps) => (
        <BankAccountStatus status={estado} />
      ),
    })
  }

  return columns
}

export const switchButtons = (t: $TSMigrationFixMe) => [
  {
    Icon: Grid,
    id: "GRID",
    tooltip: `${t("bancos.switch.gridButton")}`,
  },
  {
    Icon: Menu,
    id: "LIST",
    tooltip: `${t("bancos.switch.listButton")}`,
  },
]

export const getOptions = (
  handleDelete: (id?: string) => void,
  handleEdit: (bank: GetCuentasBancariasByFiltersDtoProps) => void,
  handleOpenRevokeAuthorizationModal: (id?: string) => void,
  handleRefreshList: (bank: GetCuentasBancariasByFiltersDtoProps) => void,
  getNotificationIdCompleted: (id: string) => boolean,
  handleReglasConciliacion: (iban: string) => void,
  handlePropuestasConciliacion: (id: string, iban?: string) => void,
  showRefresh = true,
  bank: GetCuentasBancariasByFiltersDtoProps,
  t: TFunction
): MenuItemOptions[] => [
  {
    title: `${t("bancos.bankCard.editButton")}`,
    hasRedirect: false,
    icon: faPen,
    className: "tw-text-text-50",
    action: () => handleEdit(bank),
  },
  {
    title: `${t("bancos.bankCard.reglasConciliacionButton")}`,
    hasRedirect: false,
    icon: faFileLines,
    className: "tw-text-text-50",
    action: () => handleReglasConciliacion(bank.iban ?? ""),
  },
  {
    title: `${t("bancos.bankCard.propuestasConciliacionButton")}`,
    hasRedirect: false,
    icon: faFileLines,
    className: "tw-text-text-50",
    action: () => handlePropuestasConciliacion(bank.id ?? "", bank.iban ?? ""),
  },
  {
    title: `${t("bancos.idView.updateButton")}`,
    hasRedirect: false,
    icon: faRotate,
    show: showRefresh && !bank.necesitaValidacion,
    isLoading: bank.id ? getNotificationIdCompleted(bank.id) : false,
    disabled: bank.id ? getNotificationIdCompleted(bank.id) : false,
    className: "tw-text-text-50",
    action: () => handleRefreshList(bank),
  },
  {
    title: `${t("bancos.bankCard.revokeAuthorization")}`,
    hasRedirect: false,
    icon: faLinkSlash,
    show: !bank.necesitaValidacion,
    className: "tw-text-text-50",
    action: () => handleOpenRevokeAuthorizationModal(bank?.id),
  },
  {
    title: `${t("bancos.bankCard.deleteButton")}`,
    hasRedirect: false,
    icon: faTrashCan,
    className: "tw-text-danger-20",
    action: () => handleDelete(bank?.id),
  },
]

const keys = ["institutionId", "iban"]

export const MODAL_TYPE_KEYS = {
  REDIRECT: "REDIRECT",
  RETURN_TO_ERP: "RETURN",
}

export const MODAL_TYPE_LOADING_SETUP = (t: $TSMigrationFixMe) => {
  return {
    [MODAL_TYPE_KEYS.REDIRECT]: {
      title: "",
      message: `${t("bancos.modal.loadingCard.redirectBank")}`,
      type: MODAL_TYPE_KEYS.REDIRECT,
      icon: "building-columns",
      url: "",
    },
    [MODAL_TYPE_KEYS.RETURN_TO_ERP]: {
      title: `${t("bancos.modal.loadingCard.importTitle")}`,
      message: `${t("bancos.modal.loadingCard.importBank")}`,
      type: MODAL_TYPE_KEYS.RETURN_TO_ERP,
      icon: "file-import",
      url: "",
    },
  }
}

export const getModalTypeSetup = (
  nombreEntidad: $TSMigrationFixMe,
  t: $TSMigrationFixMe
) => {
  return {
    content: {
      ...MODAL_TYPE_LOADING_SETUP(t)[MODAL_TYPE_KEYS.REDIRECT],
      title: nombreEntidad,
    },
  }
}

export const simulateFakeLoading = () =>
  new Promise((res) => setTimeout(res, 3000))

export const lazyRequest = async (apiRequest: $TSMigrationFixMe) =>
  simulateFakeLoading().then(() => apiRequest)

export const handleRedirect = (destinationUrl: $TSMigrationFixMe) =>
  window.open(destinationUrl, "_self")

export const sandBoxIntitutionId = "SANDBOXFINANCE_SFIN0000" // JUST FOR TESTING PORPUSES

export const INITIAL_MODAL_AUTORIZAR_STATE: stateAutorizar = {
  status: false,
  movimientos: true,
  id: "",
  entidadBancariaNombre: null,
  iban: "",
  bic: "",
}

export const getValuesToRedirectLink = ({
  movimientos,
  id,
}: stateAutorizar): GenerarRequisitionLinkCommandProps => ({
  autorizarMovimientos: movimientos,
  cuentaBancariaId: id,
  redirectUrl: `${import.meta.env.REACT_APP_PUBLIC_URL}/tesoreria/bancos`,
})

export const orderListAccountsByDate = (list: $TSMigrationFixMe) =>
  list
    ?.sort(
      (prev: $TSMigrationFixMe, next: $TSMigrationFixMe) =>
        // @ts-expect-error TS(2362): The left-hand side of an arithmetic operation must...
        new Date(prev.bookingDate) - new Date(next.bookingDate)
    )
    .reverse()

export const buttonsData = (
  handleOpenCreateModal: $TSMigrationFixMe,
  handleOpenDeleteModal: $TSMigrationFixMe,
  rowData: $TSMigrationFixMe
) => [
  {
    className:
      "p-button-raised p-button-text p-button-secondary icon-table icon-table__simple",
    onClick: (e: $TSMigrationFixMe) => {
      e.stopPropagation()
      handleOpenCreateModal(rowData)
    },
    icon: "pen",
  },

  {
    className:
      "icon-table ml-2 p-button-raised icon-table p-button-text p-button-secondary",
    onClick: (e: $TSMigrationFixMe) => {
      e.stopPropagation()
      handleOpenDeleteModal(rowData?.id)
    },
    icon: "fa-trash-can",
  },
]

export const joinDataArrayToQueryParams = (cuentas: $TSMigrationFixMe) => {
  const array: $TSMigrationFixMe = []
  const newShapeAccounts = cuentas?.map((cuenta: $TSMigrationFixMe) => {
    return {
      iban: cuenta?.iban,
      institutionId: cuenta?.identificadorExterno,
    }
  })
  newShapeAccounts.forEach(
    (cuenta: $TSMigrationFixMe, index: $TSMigrationFixMe) => {
      keys.forEach((key) => {
        array.push(`accountInfoRequestDtos[${index}].${key}=${cuenta[key]}`)
      })
    }
  )

  return array.join("&")
}
